import React, { useState, useEffect, useCallback } from 'react';
import {
  AricaReceiptTracking, ReceiptStatus,
  fetchReceipts, restoreReceipt, fetchReceiptAuditLog,
} from '../api/client';
import { UNIFIED_COLOR } from '../lib/statusVocab';

interface Props {
  open: boolean;
  onClose: () => void;
  defaultFrom?: string;  // YYYY-MM-DD
  defaultTo?: string;
}

const STATUS_COLORS: Record<ReceiptStatus, { bg: string; fg: string }> = {
  pending:    { bg: '#f4f4f5', fg: '#52525b' },
  in_triage:  { bg: '#dbeafe', fg: '#1e40af' },
  proposed:   { bg: '#e0e7ff', fg: '#4338ca' },
  approved:   { bg: '#d1fae5', fg: '#047857' },
  registered: { bg: '#bbf7d0', fg: '#166534' },
  rejected:   { bg: '#fecaca', fg: '#b91c1c' },
  skipped:    { bg: '#e5e7eb', fg: '#374151' },
  // deferred = エラー/不足で停止中 → あなたの修正で前進 (統一語彙: 対応待ち(要修正) 橙)
  deferred:   { bg: UNIFIED_COLOR.action_required.bg, fg: UNIFIED_COLOR.action_required.fg },
};
const STATUS_LABEL: Record<ReceiptStatus, string> = {
  pending: '未処理',
  in_triage: '検証中',
  proposed: '提案済',
  approved: '承認済',
  registered: '登録済',
  rejected: '却下',
  skipped: 'skip',
  deferred: '対応待ち(要修正)',
};

const ALL_STATUSES: (ReceiptStatus | 'all')[] = ['all', 'pending', 'in_triage', 'proposed', 'approved', 'registered', 'rejected', 'skipped', 'deferred'];

export const ReceiptTrackingPanel: React.FC<Props> = ({ open, onClose, defaultFrom, defaultTo }) => {
  const [from, setFrom] = useState(defaultFrom || '2026-03-23');
  const [to, setTo] = useState(defaultTo || '2026-04-30');
  const [statusFilter, setStatusFilter] = useState<ReceiptStatus | 'all'>('all');
  const [items, setItems] = useState<AricaReceiptTracking[]>([]);
  const [byStatus, setByStatus] = useState<Record<string, number>>({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [auditRowid, setAuditRowid] = useState<number | null>(null);
  const [auditLog, setAuditLog] = useState<any[]>([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchReceipts({
        from, to,
        status: statusFilter === 'all' ? '' : statusFilter,
        limit: 500,
      });
      setItems(r.items);
      setByStatus(r.by_status);
      setTotal(r.total);
    } catch (e: any) {
      setToast(`取得失敗: ${e.message}`);
      setTimeout(() => setToast(null), 4000);
    } finally {
      setLoading(false);
    }
  }, [from, to, statusFilter]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const doRestore = useCallback(async (rowid: number) => {
    if (!window.confirm(`arica:${rowid} を pending に戻しますか？`)) return;
    try {
      await restoreReceipt(rowid);
      setToast(`✓ arica:${rowid} を復元しました`);
      refresh();
    } catch (e: any) {
      setToast(`復元失敗: ${e.message}`);
    } finally {
      setTimeout(() => setToast(null), 4000);
    }
  }, [refresh]);

  const showAudit = useCallback(async (rowid: number) => {
    setAuditRowid(rowid);
    setAuditLog([]);
    try {
      const r = await fetchReceiptAuditLog(rowid);
      setAuditLog(r.log || []);
    } catch (e: any) {
      setToast(`audit 取得失敗: ${e.message}`);
    }
  }, []);

  if (!open) return null;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000 }} />
      <div style={{
        position: 'fixed', top: '5%', left: '5%', right: '5%', bottom: '5%',
        background: '#fff', zIndex: 1001, borderRadius: '12px', padding: '20px',
        overflow: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>📋 ARICA 証憑追跡</h2>
          <button onClick={onClose} style={{
            padding: '6px 16px', border: '1px solid #d5d3d0', borderRadius: '6px',
            background: '#fff', cursor: 'pointer', fontSize: '0.8rem',
          }}>閉じる</button>
        </div>

        {/* Filter bar */}
        <div style={{ display: 'flex', gap: '12px', margin: '12px 0', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.75rem' }}>受信日: </label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ fontSize: '0.75rem', padding: '4px' }} />
          <span>〜</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ fontSize: '0.75rem', padding: '4px' }} />
          <button onClick={refresh} disabled={loading} style={{
            padding: '4px 12px', borderRadius: '4px', background: '#2e7cf6',
            color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.75rem',
          }}>{loading ? '読込中' : '更新'}</button>
          <span style={{ fontSize: '0.8rem', color: '#555', marginLeft: 'auto' }}>
            全件: <strong>{total}</strong>
          </span>
        </div>

        {/* Status tabs */}
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '10px', flexShrink: 0 }}>
          {ALL_STATUSES.map((s) => {
            const count = s === 'all' ? total : (byStatus[s] || 0);
            const c = s === 'all' ? { bg: '#e5e7eb', fg: '#374151' } : STATUS_COLORS[s];
            const active = statusFilter === s;
            return (
              <button key={s} onClick={() => setStatusFilter(s)} style={{
                padding: '4px 10px', borderRadius: '12px', fontSize: '0.7rem',
                border: active ? `2px solid ${c.fg}` : '1px solid #d5d3d0',
                background: active ? c.bg : '#fff', color: active ? c.fg : '#555',
                cursor: 'pointer', fontWeight: active ? 600 : 400,
              }}>
                {s === 'all' ? '全て' : STATUS_LABEL[s]} ({count})
              </button>
            );
          })}
        </div>

        {/* Items table */}
        <div style={{ flex: 1, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
            <thead style={{ position: 'sticky', top: 0, background: '#fafafa' }}>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '6px', textAlign: 'left' }}>受信日</th>
                <th style={{ padding: '6px', textAlign: 'left' }}>arica_rowid</th>
                <th style={{ padding: '6px', textAlign: 'left' }}>partner</th>
                <th style={{ padding: '6px', textAlign: 'right' }}>金額(暫定)</th>
                <th style={{ padding: '6px', textAlign: 'center' }}>状態</th>
                <th style={{ padding: '6px', textAlign: 'left' }}>理由</th>
                <th style={{ padding: '6px', textAlign: 'center' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => {
                const c = STATUS_COLORS[i.processing_status];
                return (
                  <tr key={i.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px' }}>{i.receive_date}</td>
                    <td style={{ padding: '6px' }}>
                      <a href={`${'https://invoice-review.system-0f7.workers.dev'}/pdf/${i.arica_rowid}`} target="_blank" rel="noreferrer" style={{ color: '#2e7cf6' }}>{i.arica_rowid}</a>
                    </td>
                    <td style={{ padding: '6px' }}>{i.partner_name || `pid:${i.partner_id}`}</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>
                      {i.amount_pdf ? `¥${i.amount_pdf.toLocaleString()}` : '—'}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
                        fontSize: '0.7rem', background: c.bg, color: c.fg, fontWeight: 600,
                      }}>{STATUS_LABEL[i.processing_status]}</span>
                    </td>
                    <td style={{ padding: '6px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={i.reason || ''}>
                      {i.reason || '—'}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center' }}>
                      {['rejected', 'skipped', 'deferred'].includes(i.processing_status) && i.reversible === 1 && (
                        <button onClick={() => doRestore(i.arica_rowid)} style={{
                          padding: '2px 8px', fontSize: '0.7rem', borderRadius: '4px',
                          background: '#fef3c7', border: '1px solid #d97706', color: '#92400e', cursor: 'pointer',
                        }}>↺ 復元</button>
                      )}
                      <button onClick={() => showAudit(i.arica_rowid)} style={{
                        padding: '2px 8px', fontSize: '0.7rem', borderRadius: '4px',
                        background: '#fff', border: '1px solid #d5d3d0', color: '#555', cursor: 'pointer', marginLeft: '4px',
                      }}>📜</button>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: '#8a8580' }}>該当なし</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {toast && (
          <div style={{
            position: 'fixed', bottom: '20px', right: '20px',
            background: '#333', color: '#fff', padding: '10px 16px',
            borderRadius: '6px', fontSize: '0.8rem', zIndex: 1002,
          }}>{toast}</div>
        )}

        {/* Audit log modal */}
        {auditRowid !== null && (
          <div onClick={() => setAuditRowid(null)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div onClick={(e) => e.stopPropagation()} style={{
              background: '#fff', borderRadius: '8px', padding: '20px', width: '600px',
              maxHeight: '70vh', overflow: 'auto',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, fontSize: '1rem' }}>arica:{auditRowid} 遷移履歴</h3>
                <button onClick={() => setAuditRowid(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}>✕</button>
              </div>
              <div style={{ marginTop: '10px', fontSize: '0.75rem' }}>
                {auditLog.length === 0 ? <div>記録なし</div> : auditLog.map((l, idx) => (
                  <div key={idx} style={{ padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ color: '#8a8580' }}>{l.changed_at}</span>
                    {' '}
                    <strong>{l.from_status || '(new)'}</strong> → <strong style={{ color: '#047857' }}>{l.to_status}</strong>
                    {' by '}{l.changed_by}
                    {l.reason && <div style={{ color: '#555', marginTop: '2px' }}>理由: {l.reason}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
